import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";

const AuthContext = createContext(null);

/** @returns {number | null} expiry time in ms since epoch, or null if not a JWT / no exp */
function getJwtExpiryMs(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1];
    const padded = payload.padEnd(
      payload.length + ((4 - (payload.length % 4)) % 4),
      "=",
    );
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const { exp } = JSON.parse(json);
    if (typeof exp !== "number") return null;
    return exp * 1000;
  } catch {
    return null;
  }
}

function getAuthHeaderValue(headers) {
  if (!headers) return null;
  if (headers instanceof Headers) {
    return (
      headers.get("Authorization") || headers.get("authorization") || null
    );
  }
  const h = headers;
  return (
    h.Authorization ||
    h.authorization ||
    h["Authorization"] ||
    h["authorization"] ||
    null
  );
}

function requestHadBearer(input, init) {
  if (input instanceof Request) {
    const v = getAuthHeaderValue(input.headers);
    if (v && String(v).startsWith("Bearer ")) return true;
  }
  const fromInit = getAuthHeaderValue(init?.headers);
  return !!(fromInit && String(fromInit).startsWith("Bearer "));
}

function getRequestUrl(input) {
  if (typeof input === "string") return input;
  if (input instanceof Request) return input.url;
  return "";
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const logoutRef = useRef(null);
  const endingSessionRef = useRef(false);

  useEffect(() => {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, [user]);

  const logout = useCallback(({ redirectToLogin = false } = {}) => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken("");
    setUser(null);
    endingSessionRef.current = false;
    if (redirectToLogin) {
      window.location.assign("/login");
    }
  }, []);

  logoutRef.current = logout;

  const login = (tokenData, userData) => {
    endingSessionRef.current = false;
    setToken(tokenData);
    setUser(userData);
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  // Proactive logout when JWT exp is reached (opaque tokens rely on 401 handling below).
  useEffect(() => {
    if (!token) return undefined;
    const expMs = getJwtExpiryMs(token);
    if (expMs == null) return undefined;
    const skewMs = 60_000;
    const delay = Math.max(expMs - Date.now() - skewMs, 0);
    const id = window.setTimeout(() => {
      logout({ redirectToLogin: true });
    }, delay);
    return () => window.clearTimeout(id);
  }, [token, logout]);

  // 401 on an authenticated request: clear session and go to login (login itself has no Bearer).
  useEffect(() => {
    const orig = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const res = await orig(input, init);
      if (res.status !== 401 || !requestHadBearer(input, init)) {
        return res;
      }
      const url = getRequestUrl(input);
      if (url.includes("/login")) {
        return res;
      }
      if (endingSessionRef.current) {
        return res;
      }
      endingSessionRef.current = true;
      logoutRef.current?.({ redirectToLogin: true });
      return res;
    };
    return () => {
      window.fetch = orig;
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{ token, user, login, logout, isAuthenticated }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
